import React from 'react';
import InlineTranslationEdit from './InlineTranslationEdit';
import RichImageCarousel from './RichImageCarousel';
import { useContent } from '../contexts/ContentContext';
import { useEditMode } from '../contexts/EditModeContext';
import useHomeSlides from '../hooks/useHomeSlides';
import RichContentRenderer from './RichContentRenderer';
import { useTheme } from '../contexts/ThemeContext';

const CONTENT_KEY = 'HomeAboutBlockText';
const EDIT_PLACEHOLDER = 'Click to edit organization writeup';

export default function OrganizationWriteupSection({
  mainDraw,
  gameType,
  slides,
  editMode: editModeProp,
  handleTranslationUpdate,
  contentKey = CONTENT_KEY,
  editPlaceholder = EDIT_PLACEHOLDER,
  showSlides: showSlidesProp = true,
  variant = 'default',
  className = '',
}) {
  const { c, resolveContentValue } = useContent();
  const editContext = useEditMode();
  const editMode = editModeProp ?? editContext.editMode;
  const onUpdate = handleTranslationUpdate || editContext.handleTranslationUpdate;
  const hookSlides = useHomeSlides(mainDraw, { gameType });
  const allowSlides = showSlidesProp !== false;
  const resolvedSlides = allowSlides ? (Array.isArray(slides) ? slides : hookSlides) : [];
  const hasSlides = resolvedSlides.length > 0;

  const rawValue = c(contentKey, '');
  const hasContent = stripHtml(rawValue).length > 0;
  const resolvedHtml = resolveContentValue(rawValue, {
    currentKey: contentKey,
    editMode,
  });

  if (!editMode && !hasContent && !hasSlides) return null;

  const showWriteup = hasContent || editMode;
  const showSlides = hasSlides;
  const gridClass = showWriteup && showSlides ? 'lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.92fr)]' : 'lg:grid-cols-1';
  const containerClass = 'max-w-7xl';
  const articleClass = 'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-7 lg:p-8';

  let isDark = false;
  try {
    const { theme } = useTheme() || {};
    isDark = theme === 'dark';
  } catch (e) {}

  if (variant === 'cinematic') {
    const glassClass = isDark
      ? 'rounded-2xl bg-white/[0.03] backdrop-blur-sm border border-white/10'
      : 'rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg border border-gray-200';
    return (
      <article className={`${glassClass} p-5 sm:p-7 lg:p-8 flex-1 h-full flex flex-col justify-center ${className}`.trim()}>
        {editMode ? (
          <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 w-full">
            <InlineTranslationEdit
              translationKey={contentKey}
              value={rawValue}
              fallback={editPlaceholder}
              onUpdate={onUpdate}
              editMode={editMode}
              multiline
            />
          </div>
        ) : (
          <RichContentRenderer
            html={resolvedHtml}
            className="text-gray-700 dark:text-gray-300 w-full"
          />
        )}
      </article>
    );
  }

  return (
    <section className="bg-white dark:bg-gray-950 py-8 sm:py-10">
      <div className={`${containerClass} mx-auto px-4 sm:px-6 lg:px-8`}>
        <div className={`grid grid-cols-1 ${gridClass} gap-5 lg:gap-8 items-stretch`}>
          {showWriteup && (
            <article className={articleClass}>
              {editMode ? (
                <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                  <InlineTranslationEdit
                    translationKey={contentKey}
                    value={rawValue}
                    fallback={editPlaceholder}
                    onUpdate={onUpdate}
                    editMode={editMode}
                    multiline
                  />
                </div>
              ) : (
                <RichContentRenderer
                  html={resolvedHtml}
                  className="text-gray-700 dark:text-gray-300"
                />
              )}
            </article>
          )}

          {showSlides && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shadow-sm p-3 sm:p-4 flex items-center">
              <RichImageCarousel slides={resolvedSlides} className="shadow-none" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function stripHtml(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
}
